import { Link } from "react-router-dom";
import { ArrowRight, ArrowDownToLine, Send, Wallet, Repeat, Users, Webhook, KeyRound, FlaskConical, BookOpen, Code2 } from "lucide-react";
import Reveal from "../components/Reveal";
import CodeEditor from "../components/home/CodeEditor";
import CtaSection from "../components/CtaSection";

const apis = [
  { icon: ArrowDownToLine, name: "Payments API", desc: "Create and manage incoming payments and collections." },
  { icon: Send, name: "Payouts API", desc: "Send individual or bulk payouts to supported destinations." },
  { icon: Wallet, name: "Accounts API", desc: "Read balances, account details and transaction history." },
  { icon: Repeat, name: "FX API", desc: "Convert between supported currencies and track conversions." },
  { icon: Users, name: "Beneficiaries API", desc: "Create and manage payout beneficiaries." },
  { icon: Webhook, name: "Webhooks", desc: "Receive real-time events for payment and transfer status changes." },
];

const steps = [
  { icon: KeyRound, title: "Get API keys", desc: "Request access and receive sandbox API keys for your workspace." },
  { icon: FlaskConical, title: "Build in sandbox", desc: "Integrate and test the full money movement flow safely." },
  { icon: Code2, title: "Go live", desc: "Move to production credentials when your integration is ready." },
];

const Developers = () => (
  <div data-testid="developers-page">
    <section className="border-b border-[#E2E8F0] bg-[#0B1120] py-20 md:py-28">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6BA3E8]">Developers</p>
          <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Build the future of money movement.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
            Simple APIs. Powerful infrastructure. Global possibilities.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              data-testid="dev-read-docs"
              className="group flex items-center justify-center gap-2 rounded-full bg-[#2E7CD6] px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#2568b8]"
            >
              Read Documentation
              <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/contact"
              data-testid="dev-get-api-access"
              className="flex items-center justify-center rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              Get API Access
            </Link>
          </div>
          <p className="mt-6 text-xs text-slate-500">Developer platform shown as planned capability. Request access to learn more.</p>
        </Reveal>
        <Reveal delay={0.15}>
          <CodeEditor />
        </Reveal>
      </div>
    </section>

    <section id="apis" className="scroll-mt-24 bg-white py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-[#0F172A] md:text-4xl">API overview</h2>
          <p className="mt-4 text-base text-[#334155] md:text-lg">
            A consistent REST API across the money movement journey — secured with API key authentication and designed for predictable integration.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {apis.map((a, i) => (
            <Reveal key={a.name} delay={(i % 3) * 0.08}>
              <div className="h-full rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/[0.06]" data-testid={`api-card-${a.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EFF5FD] text-[#1E3A6E]">
                  <a.icon size={20} strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-[#0F172A]">{a.name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#64748B]">{a.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[#F8FAFC] py-20 md:py-28">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <Reveal>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-[#0F172A] md:text-4xl">Authentication, done properly.</h2>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-[#334155]">
            Every request is authenticated with API keys scoped to your workspace. Keys are environment-specific, so sandbox and production stay cleanly separated.
          </p>
          <ul className="mt-7 space-y-3.5">
            {["API key authentication on every request", "Separate sandbox and production credentials", "Signed webhook events for verification"].map((f) => (
              <li key={f} className="flex items-start gap-3 text-sm text-[#334155]">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <KeyRound size={11} strokeWidth={2} />
                </span>
                {f}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-[#0F1B33] shadow-xl">
            <div className="border-b border-white/10 px-5 py-3.5">
              <span className="font-mono text-xs text-slate-400">Authenticated request</span>
            </div>
            <pre className="overflow-x-auto p-5 font-mono text-[13px] leading-7 text-slate-300">{`curl https://api.swiftwire.com/v1/transfers \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 2500,
    "currency": "USD",
    "destination": { "country": "NG", "type": "bank" }
  }'`}</pre>
          </div>
        </Reveal>
      </div>
    </section>

    <section id="sandbox" className="scroll-mt-24 bg-white py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-[#0F172A] md:text-4xl">From sandbox to production.</h2>
          <p className="mt-4 text-base text-[#334155] md:text-lg">A clear path from first API call to live money movement.</p>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <div className="h-full rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-sm" data-testid={`dev-step-${i + 1}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EFF5FD] text-[#1E3A6E]">
                  <s.icon size={20} strokeWidth={1.5} />
                </span>
                <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-[#64748B]">Step {i + 1}</p>
                <h3 className="mt-1.5 font-heading text-lg font-bold text-[#0F172A]">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#64748B]">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12 flex justify-center">
          <div id="webhooks" className="flex scroll-mt-24 flex-col items-center gap-4 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] px-8 py-7 sm:flex-row sm:gap-6">
            <BookOpen size={22} strokeWidth={1.5} className="text-[#2E7CD6]" />
            <p className="text-center text-sm text-[#334155] sm:text-left">
              Full API reference, webhook event catalog and sandbox guides are available with developer access.
            </p>
            <Link to="/contact" data-testid="dev-docs-cta" className="group inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-[#1E3A6E]">
              Request access
              <ArrowRight size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    <CtaSection title="Start building on Swiftwire." subtitle="Get sandbox access and explore the money movement APIs." />
  </div>
);

export default Developers;
